import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode, useRef } from 'react';
import apiService from '../services/api';
import { Session, Sessions, Chunk, SessionContextType } from '../types';
import { useAuth } from './AuthContext';

const SessionContext = createContext<SessionContextType | undefined>(undefined);

export const useSession = (): SessionContextType => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
};

interface SessionProviderProps {
  children: ReactNode;
}

// Internal provider that has access to auth
const SessionProviderInternal: React.FC<SessionProviderProps> = ({ children }) => {
  const auth = useAuth();
  const [currentSessionId, setCurrentSessionIdState] = useState<string>('session1');
  const [sessions, setSessions] = useState<Sessions>({});
  const [userId, setUserIdState] = useState<string>('');
  const searchRefreshCallbackRef = useRef<(() => void) | null>(null);
  const selectedCountRefreshCallbackRef = useRef<((action: 'increment' | 'decrement' | 'refresh') => void) | null>(null);

  // Wrapper to persist currentSessionId to backend
  const setCurrentSessionId = useCallback((sessionId: string) => {
    setCurrentSessionIdState(sessionId);
    if (userId) {
      // Persist to backend asynchronously
      apiService.setCurrentSession(userId, sessionId).catch(error => {
        console.error('Error persisting current session to backend:', error);
      });
    }
  }, [userId]);

  // Sync userId with authenticated Google user on mount and when auth changes
  // This is the single source of truth for userId synchronization
  useEffect(() => {
    if (auth.isAuthenticated && auth.user) {
      const googleUserId = auth.user.sub;
      if (userId !== googleUserId) {
        setUserIdState(googleUserId);
      }
    } else if (!auth.isAuthenticated && !auth.isLoading) {
      // Auth has been cleared (logged out or session expired)
      // Reset session state to prevent stale data
      if (userId) {
        setUserIdState('');
        setSessions({});
        setCurrentSessionIdState('session1');
      }
    }
    // userId is intentionally excluded from deps to prevent potential loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isAuthenticated, auth.user, auth.isLoading]);

  const createNewSession = useCallback((name = 'New Session'): Session => {
    return {
      id: Date.now().toString(),
      name,
      createdAt: new Date().toISOString(),
      user_id: userId,
      lastSearchQuery: '',
      lastSearchFilters: {
        source: '',
        includeDone: false,
        includeDiscarded: false,
      }
    };
  }, [userId]);

  const initializeDefaultSession = useCallback((): void => {
    const defaultSession = createNewSession('Session 1');
    setSessions({ session1: defaultSession });
    setCurrentSessionId('session1');
  }, [createNewSession, setCurrentSessionId]); // createNewSession already depends on userId

  // Initialize default session
  useEffect(() => {
    // Don't load sessions if userId is not set yet
    if (!userId) {
      return;
    }

    const loadSessionsFromBackend = async (): Promise<void> => {
      try {
        // Clear sessions immediately when userId changes to prevent cross-contamination
        setSessions({});

        const response = await apiService.getSessions(userId);
        const backendSessions = response.data.sessions;

        if (Object.keys(backendSessions).length > 0) {
          // Ensure all sessions have the correct user_id
          const sessionsWithCorrectUserId = Object.entries(backendSessions).reduce((acc, [sessionId, session]) => {
            acc[sessionId] = {
              ...session,
              user_id: userId // Override with current userId to fix any mismatches
            };
            return acc;
          }, {} as Sessions);

          setSessions(sessionsWithCorrectUserId);

          // Try to restore persisted session ID from backend, fallback to first available session
          const persistedSessionId = response.data.current_session_id;
          const sessionIds = Object.keys(sessionsWithCorrectUserId);

          if (persistedSessionId && sessionIds.includes(persistedSessionId)) {
            setCurrentSessionIdState(persistedSessionId);
          } else {
            const firstSessionId = sessionIds[0];
            setCurrentSessionIdState(firstSessionId);
            // Only persist if persistedSessionId is truthy but invalid (not in sessionIds)
            // Avoid unnecessary API call when persistedSessionId is null/undefined
            if (persistedSessionId) {
              apiService.setCurrentSession(userId, firstSessionId).catch(error => {
                console.error('Error persisting default session to backend:', error);
              });
            }
          }
        } else {
          initializeDefaultSession();
        }
      } catch (error) {
        console.error('Error loading sessions from backend:', error);
        initializeDefaultSession();
      }
    }

    loadSessionsFromBackend();
  }, [userId, initializeDefaultSession]);

  // Save sessions to backend whenever sessions change (but not when empty - that means we're loading)
  // Note: We only trigger on sessions changes, not userId changes, to avoid saving
  // old user's sessions under new user's ID during profile switching
  useEffect(() => {
    const sessionCount = Object.keys(sessions).length;
    // Don't save if userId is not set or sessions are empty
    if (sessionCount > 0 && userId) {
      apiService.saveSessions(sessions, userId).catch(error => {
        console.error('Error saving sessions to backend:', error);
      });
    }
  }, [sessions, userId]); // Include userId since it's used in the effect

  const addSession = (name: string): string => {
    const sessionId = Date.now().toString();
    const newSession: Session = {
      id: sessionId,
      name,
      createdAt: new Date().toISOString(),
      user_id: userId,
      lastSearchQuery: '',
      lastSearchFilters: {
        source: '',
        includeDone: false,
        includeDiscarded: false,
      }
    };
    setSessions(prev => ({ ...prev, [sessionId]: newSession }));
    // Immediately switch to the new session
    setCurrentSessionId(sessionId);
    return sessionId;
  };

  const switchSession = (sessionId: string): void => {
    if (sessions[sessionId]) {
      setCurrentSessionId(sessionId);

      // Auto-refresh search if the new session has a lastSearchQuery
      const newSession = sessions[sessionId];
      if (newSession.lastSearchQuery && searchRefreshCallbackRef.current) {
        // Use setTimeout to ensure the session switch completes first
        setTimeout(() => {
          searchRefreshCallbackRef.current?.();
        }, 0);
      }
    }
  };

  const deleteSession = (sessionId: string): boolean => {
    // Don't allow deleting if it's the only session
    if (Object.keys(sessions).length <= 1) {
      return false;
    }

    // Clean up session references from Pinecone metadata
    apiService.cleanupSession(sessionId, userId).catch(error => {
      console.error('Error cleaning up session from chunks:', error);
      // Continue with deletion even if cleanup fails
    });

    // If deleting the current session, switch to the first available session
    if (sessionId === currentSessionId) {
      const remainingSessions = Object.keys(sessions).filter(id => id !== sessionId);
      setCurrentSessionId(remainingSessions[0]);
    }

    // Remove the session
    setSessions(prev => {
      const newSessions = { ...prev };
      delete newSessions[sessionId];
      return newSessions;
    });

    return true;
  };

  const updateSession = (sessionId: string, updates: Partial<Session>): void => {
    setSessions(prev => {
      if (!prev[sessionId]) {
        return prev;
      }
      return {
        ...prev,
        [sessionId]: {
          ...prev[sessionId],
          ...updates
        }
      };
    });
  };

  const getCurrentSession = (): Session | null => {
    return sessions[currentSessionId] || Object.values(sessions)[0] || null;
  };

  // API-based chunk operations
  const markChunkSelected = useCallback(async (chunkId: string): Promise<void> => {
    // Optimistically trigger refresh of selected count in navigation (increment)
    if (selectedCountRefreshCallbackRef.current) {
      selectedCountRefreshCallbackRef.current('increment');
    }

    try {
      await apiService.markSelected(chunkId, currentSessionId, userId);
      // The optimistic increment is sufficient - don't refresh to avoid flicker
    } catch (error) {
      console.error('Error marking chunk as selected:', error);
      // Rollback: trigger refresh to get actual count from backend
      if (selectedCountRefreshCallbackRef.current) {
        selectedCountRefreshCallbackRef.current('refresh');
      }
      throw error;
    }
  }, [currentSessionId, userId]);

  const unmarkChunkSelected = useCallback(async (chunkId: string): Promise<void> => {
    // Optimistically trigger refresh of selected count in navigation (decrement)
    if (selectedCountRefreshCallbackRef.current) {
      selectedCountRefreshCallbackRef.current('decrement');
    }

    try {
      await apiService.unmarkSelected([chunkId], currentSessionId, userId);
      // The optimistic decrement is sufficient - don't refresh to avoid flicker
    } catch (error) {
      console.error('Error unmarking chunk as selected:', error);
      // Rollback: trigger refresh to get actual count from backend
      if (selectedCountRefreshCallbackRef.current) {
        selectedCountRefreshCallbackRef.current('refresh');
      }
      throw error;
    }
  }, [currentSessionId, userId]);

  const markChunkDiscarded = useCallback(async (chunkId: string): Promise<void> => {
    try {
      await apiService.markDiscarded(chunkId, currentSessionId, userId);
    } catch (error) {
      console.error('Error marking chunk as discarded:', error);
      throw error;
    }
  }, [currentSessionId, userId]);

  const getSelectedChunks = useCallback(async (): Promise<Chunk[]> => {
    try {
      const response = await apiService.getSelectedChunks(currentSessionId, userId);
      return response.data.matches;
    } catch (error) {
      console.error('Error getting selected chunks:', error);
      return [];
    }
  }, [currentSessionId, userId]);

  const getDiscardedChunks = useCallback(async (): Promise<Chunk[]> => {
    try {
      const response = await apiService.getDiscardedChunks(currentSessionId, userId);
      return response.data.matches;
    } catch (error) {
      console.error('Error getting discarded chunks:', error);
      return [];
    }
  }, [currentSessionId, userId]);

  const clearSelectedChunks = useCallback(async (): Promise<void> => {
    try {
      // First get all selected chunks
      const selectedChunks = await getSelectedChunks();
      const chunkIds = selectedChunks.map(chunk => chunk.id);

      if (chunkIds.length > 0) {
        // Update Pinecone to remove session from selected_sessions metadata
        await apiService.unmarkSelected(chunkIds, currentSessionId, userId);
      }

      // Trigger refresh of selected count in navigation
      if (selectedCountRefreshCallbackRef.current) {
        selectedCountRefreshCallbackRef.current('refresh');
      }
    } catch (error) {
      console.error('Error clearing selected chunks:', error);
      throw error;
    }
  }, [currentSessionId, userId, getSelectedChunks]);

  const unmarkChunkSelectedConditionally = useCallback(async (chunkId: string): Promise<void> => {
    // First check if the chunk is actually selected before unmarking
    try {
      const selectedChunks = await getSelectedChunks();
      const isSelected = selectedChunks.some(chunk => chunk.id === chunkId);

      if (isSelected) {
        // Only unmark and decrement if chunk was actually selected
        await unmarkChunkSelected(chunkId);
      } else {
        // Chunk wasn't selected, just call the API without updating the count
        await apiService.unmarkSelected([chunkId], currentSessionId, userId);
      }
    } catch (error) {
      console.error('Error conditionally unmarking chunk:', error);
      throw error;
    }
  }, [currentSessionId, userId, getSelectedChunks, unmarkChunkSelected]);

  const setUserId = (id: string): void => {
    setUserIdState(id);
  };

  const resetUserId = (): void => {
    setUserIdState('');
  };

  const registerSearchRefreshCallback = useCallback((callback: () => void): void => {
    searchRefreshCallbackRef.current = callback;
  }, []);

  const unregisterSearchRefreshCallback = useCallback((): void => {
    searchRefreshCallbackRef.current = null;
  }, []);

  const registerSelectedCountRefreshCallback = useCallback((callback: (action: 'increment' | 'decrement' | 'refresh') => void): void => {
    selectedCountRefreshCallbackRef.current = callback;
  }, []);

  const unregisterSelectedCountRefreshCallback = useCallback((): void => {
    selectedCountRefreshCallbackRef.current = null;
  }, []);

  const value: SessionContextType = {
    sessions,
    currentSessionId,
    currentSession: getCurrentSession(),
    userId,
    setUserId,
    resetUserId,
    addSession,
    switchSession,
    deleteSession,
    updateSession,
    markChunkSelected,
    unmarkChunkSelected,
    unmarkChunkSelectedConditionally,
    markChunkDiscarded,
    getSelectedChunks,
    getDiscardedChunks,
    clearSelectedChunks,
    registerSearchRefreshCallback,
    unregisterSearchRefreshCallback,
    registerSelectedCountRefreshCallback,
    unregisterSelectedCountRefreshCallback
  };

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  );
};
// Export wrapper that works with or without AuthProvider (for tests)
export const SessionProvider: React.FC<SessionProviderProps> = ({ children }) => {
  return <SessionProviderInternal>{children}</SessionProviderInternal>;
};
